import FormBase from '@/components/forms/new/FormBase';
import { Box } from '@chakra-ui/react';
import ServiceProfileVisibleScope from './formFields/ServiceProfileVisibleScope';
import ServiceProfileRegions from './formFields/ServiceProfileRegions';
// import ServiceProfileNameField from './formFields/ServiceProfileNameField';
import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import updateServiceProfiles from 'API/ServiceProfiles/updateServiceProfiles';
import { multiSelectDataMapper, toSentenceCase } from 'utils/utils';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import createServiceProfile from 'API/ServiceProfiles/createServiceProfile';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import getWebexServiceProfile from 'API/ServiceProfiles/getWebexServiceProfile';
import getWebexServiceProfileRegions from 'API/ServiceProfiles/getWebexServiceProfileRegions';
import useFieldValidation from 'hooks/useFieldValidation';

import { validationSchemaDR } from './schema';

import BreadcrumbTrail from 'components/BreadcrumbTrail';

const WebexServiceProfile = ({
    mode,
    data,
    level,
    handleBackButton,
    visibleScopeOptions,
}) => {
    const [countriesListsEnum, setcountriesListsEnum] = useState([]);
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        currentWholesalerData,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { requiredScope } = useSelector((state) => state.login);

    const { darkMode } = useSelector((state) => state.settings);

    const DEFAULT_WEBEX_PAYLOAD = {
        name: 'Webex Wholesaler Profile',
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        countriesLists: [],
        visibleScope: level === 'partner' ? 20 : 60,
    };

    useEffect(() => {
        if (
            mode !== 'EDIT' &&
            level !== 'partner' &&
            currentWholesalerData?.name
        ) {
            handleChangeState({
                name: `${currentWholesalerData?.name} - Webex Wholesaler Profile`,
            });
        } else if (
            mode !== 'EDIT' &&
            level === 'partner' &&
            companiesAndPartners?.length > 0
        ) {
            const partnerName = companiesAndPartners.find(
                ({ id }) => id === partnerID,
            )?.name;
            handleChangeState({
                name: `${partnerName} - Webex Partner Profile`,
            });
        }
    }, [
        mode,
        companiesAndPartners,
        currentWholesalerData?.name,
        level,
        wholesalerID,
        partnerID,
        data?.wholesalerID,
        data?.partnerID,
    ]);

    const extractErrorMessage = (e) =>
        e?.response?.data?.message ||
        e?.message ||
        'An error occurred. Please try again later.';

    const [modalState, setModalState] = useState(data || DEFAULT_WEBEX_PAYLOAD);
    const [error, setError] = useState(''); // keep as string
    const [isLoading, setIsLoading] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    useFieldValidation(modalState, validationSchemaDR);

    const scope = useCallback(() => {
        if (level === 'partner') {
            return `Partner/${data?.partnerID || partnerID}`;
        } else {
            return `Wholesaler/${data?.wholesalerID || wholesalerID}`;
        }
    }, [level, partnerID, wholesalerID, data?.wholesalerID, data?.partnerID]);
    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    const handleGetDropdown = useCallback(() => {
        setIsLoading(true);
        const fetchSPData = mode === 'EDIT' && data;
        Promise.all([
            fetchSPData
                ? getWebexServiceProfile({ scope: scope() })
                : setTimeout(() => {
                      Promise.resolve();
                  }, 1),
            getWebexServiceProfileRegions({ scope: scope() }),
        ])
            .then(([spResult, countriesListsResult]) => {
                setcountriesListsEnum(
                    countriesListsResult.data.map(multiSelectDataMapper),
                );
                if (fetchSPData) {
                    const countriesLists = spResult.data.countriesLists.map(
                        multiSelectDataMapper,
                    );
                    setModalState({
                        ...spResult.data,
                        countriesLists,
                    });
                }
            })
            .catch((e) => {
                setError(extractErrorMessage(e));
            })
            .finally(() => {
                setTimeout(() => setIsLoading(false), 500);
            });
    }, [mode, data, scope]);

    useEffect(() => {
        handleGetDropdown();
    }, [mode, data, level, handleGetDropdown]);

    if (isLoading) {
        return <LoadingIndicator />;
    }

    // Helper to map value(s) to option(s) for select fields
    const mapToOptions = (values, options) => {
        if (!values || !options) return [];
        if (Array.isArray(values)) {
            return (
                values
                    .map((v) => options.find((o) => o.value === (v.value ?? v)))
                    ?.filter(Boolean) || []
            );
        }
        return (
            options.find((o) => o.value === (values.value ?? values)) || null
        );
    };
    const initialValues = {
        name: modalState?.name || '',
        countriesLists: mapToOptions(
            modalState?.countriesLists,
            countriesListsEnum,
        ),
        partnerID:
            level === 'partner'
                ? (modalState?.partnerID ?? partnerID)
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (modalState?.wholesalerID ?? wholesalerID),
        visibleScope:
            modalState?.visibleScope ?? (level === 'partner' ? 20 : 60),
    };

    const finalFormSubmit = async (values, actions) => {
        setSubmitted(true);
        setError('');
        setIsLoading(true);
        try {
            const payload = {
                type: 'Webex',
                payload: modalState,
                level,
                scope: requiredScope,
            };
            if (mode === 'EDIT') {
                await updateServiceProfiles(payload);
            } else {
                await createServiceProfile(payload);
            }
            handleBackButton();
        } catch (e) {
            setError(extractErrorMessage(e));
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];

    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`${toSentenceCase(mode)} ${modalState.name || data?.name}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="webexServiceProfileBackBtn"
                ufSaveBtn="webexServiceProfileSaveBtn"
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}>
                {({ errors = {}, touched = {} }) =>
                    [
                        // <ServiceProfileNameField
                        //     key="name"
                        //     errors={errors}
                        //     touched={touched}
                        //     name="name"
                        //     darkMode={darkMode}
                        // />,
                        <ServiceProfileVisibleScope
                            key="webexServiceProfileVisibleScope"
                            options={visibleScopeOptions}
                            darkMode={darkMode}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    visibleScope: e.value,
                                }));
                            }}
                        />,
                        <ServiceProfileRegions
                            key="webexServiceProfileRegions"
                            name="regions"
                            value={modalState.countriesLists}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    countriesLists: val,
                                }))
                            }
                            options={countriesListsEnum}
                            darkMode={darkMode}
                            error={
                                !modalState.countriesLists ||
                                modalState.countriesLists.length === 0
                                    ? 'At least one region is required'
                                    : ''
                            }
                            showError={submitted}
                            disableStackHeight={true}
                            errors={errors}
                            touched={touched}
                        />,
                        error && (
                            <Box paddingBottom="0.5rem" key="error-box-webex">
                                <Alert
                                    key="error"
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
};

export default WebexServiceProfile;
